export enum LogLevel {
  DEBUG = 1,
  INFO = 2,
  WARN = 3,
  ERROR = 4,
  OFF = 5
}

export class Logger {

  private static _configuredLevel: LogLevel = LogLevel.WARN;
  private static _overrideLevel: LogLevel | null = null;
  private static _initialized = false;

  constructor(protected prefix: string) {
    Logger.ensureInitialized();
  }

  static configure(enableDebug: boolean): void {
    Logger._configuredLevel = enableDebug ? LogLevel.DEBUG : LogLevel.WARN;
    Logger._overrideLevel = Logger.resolveOverride();
    Logger._initialized = true;
  }

  static getEffectiveLevel(): LogLevel {
    Logger.ensureInitialized();
    return Logger._overrideLevel ?? Logger._configuredLevel;
  }

  static isDebugEnabled(): boolean {
    return Logger.getEffectiveLevel() <= LogLevel.DEBUG;
  }

  private static ensureInitialized(): void {
    if (!Logger._initialized) {
      Logger._overrideLevel = Logger.resolveOverride();
      Logger._initialized = true;
    }
  }

  private static resolveOverride(): LogLevel | null {
    try {
      if (typeof window === 'undefined') return null;

      const ls = window.localStorage?.getItem('DEBUG');
      if (ls === 'true' || ls === '1') return LogLevel.DEBUG;
      if (ls === 'false' || ls === '0' || ls === 'off') return LogLevel.OFF;

      const params = new URLSearchParams(window.location.search);
      if (params.has('debug')) {
        const val = params.get('debug');
        if (val === '0' || val === 'false' || val === 'off') return LogLevel.OFF;
        return LogLevel.DEBUG;
      }
    } catch {
      // localStorage / URL parsing failures: fall through to configured level
    }
    return null;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= Logger.getEffectiveLevel();
  }

  log(...args: any[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log(`[${this.prefix}]`, ...args);
    }
  }

  debug(...args: any[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.debug(`[${this.prefix}]`, ...args);
    }
  }

  info(...args: any[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(`[${this.prefix}]`, ...args);
    }
  }

  warn(...args: any[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(`[${this.prefix}]`, ...args);
    }
  }

  error(...args: any[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(`[${this.prefix}]`, ...args);
    }
  }

  setLogLevel(_level: LogLevel): void {
    // Per-instance overrides removed; effective level is resolved globally.
    // Kept for backwards compatibility with existing callers.
  }

  getLogLevel(): LogLevel {
    return Logger.getEffectiveLevel();
  }
}

export function installConsoleFilter(): void {
  if (typeof window === 'undefined') return;
  if ((window as any).__consoleFilterInstalled) return;

  const noop = () => {};
  const originals = {
    log: console.log.bind(console),
    debug: console.debug.bind(console),
    info: console.info.bind(console),
    trace: console.trace.bind(console),
    group: console.group.bind(console),
    groupCollapsed: console.groupCollapsed.bind(console),
    groupEnd: console.groupEnd.bind(console),
  };

  const apply = () => {
    const debugOn = Logger.isDebugEnabled();
    console.log = debugOn ? originals.log : noop;
    console.debug = debugOn ? originals.debug : noop;
    console.info = debugOn ? originals.info : noop;
    console.trace = debugOn ? originals.trace : noop;
    console.group = debugOn ? originals.group : noop;
    console.groupCollapsed = debugOn ? originals.groupCollapsed : noop;
    console.groupEnd = debugOn ? originals.groupEnd : noop;
  };

  apply();

  (window as any).__consoleFilterInstalled = true;
  (window as any).__refreshConsoleFilter = apply;
}
